import SetColumnOrderDetails from "components/order/ColumnOrder/SetColumnOrderDetails";
import SetOrderHeader from "components/order/SetOrderHeader";
import SetOrderFooter from "components/order/SetOrderFooter";
import LoadTemplateModal from "components/order/LoadTemplateModal";
import SaveTemplateModal from "components/order/SaveTemplateModal";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useCallback, useMemo, useState } from "react";
import { useOneAgorder, useCreateOrder, useUpdateAgorder } from "http-hooks/agorder/useAgorder";
import { useOrderHeader } from "hooks/order";
import useAuth from "hooks/useAuth";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import useFullScreenLayout from "hooks/useFullScreenLayout";
import { getFormType, getOrdTypFromFormType } from "utils/orderHelpers";

export default function ColumnOrder() {
  useFullScreenLayout();

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { agttp } = useAuth();
  const { isOrderEntryEnabled } = useCompanyPreferences();

  // Order template modal states
  const [isLoadTemplateOpen, setIsLoadTemplateOpen] = useState(false);
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);

  // Get formtype from query param (G/P/S)
  const formtype = searchParams.get('formtype');
  const formType = useMemo(() => getFormType(agttp, formtype), [agttp, formtype]);
  const ordTyp = useMemo(() => getOrdTypFromFormType(agttp, formtype), [agttp, formtype]);
  
  // Get orderId from location.state or query parameters
  const id = useMemo(() => {
    if (location.state?.id) {
      return location.state.id;
    }
    return searchParams.get('orderId');
  }, [location.state?.id, searchParams]);
  
  // Global order header state
  const { 
    orderHeader, 
    setOrderHeader,
    updateOrderHeader,
    lotData, 
    setLotData,
    columnOrderItems,
    setColumnOrderItems,
    populateColumnOrderItemsFromOrder,
    updateSelectBox,
    selectBoxes,
    setPricelistFilters,
    sizeToColumnMap
  } = useOrderHeader();

  const orderEntryBlocked = !isOrderEntryEnabled();
  
  const { data, isLoading: isOrderLoading } = useOneAgorder(id, !!id && !orderEntryBlocked, { 
    show_items: !!id,
    isColumnOrder: true
  });

  // Create order mutation
  const createOrderMutation = useCreateOrder({});

  // Update order mutation
  const updateOrderMutation = useUpdateAgorder({});

  // Load all order data into global store when available
  useEffect(() => {
    if (data && sizeToColumnMap) {
      setOrderHeader({
        custom_ordno: data.custom_ordno || "",
        ordno: data.ordno || "",
        ordate: data.ordate,
        ordertyp: data.ordertyp || "G",
        ordtyp: data.ordtyp || ordTyp,
        ordcusid: data.ordcusid,
        ordvendid: data.ordvendid,
        ordtptid: data.ordtptid,
        orddespatch: data.orddespatch,
        orderedby: data.orderedby,
        pldate: data.pldate,
        prntp: data.prntp,
        is_scheme: data.is_scheme || 0,
        agent_salesman_code: data.agent_salesman_code || "",
        private_marka: data.private_marka || "",
        ordremark1: data.ordremark1 || "",
        ordremark2: data.ordremark2 || "",
      });

      // Set dropdown selections using populated data
      if (data.customer) {
        updateSelectBox('customer', data.customer);
      }
      
      if (data.vendor) {
        updateSelectBox('vendor', data.vendor);
        // Set pricelist filters for vendor - always set this for proper pricelist loading
        setPricelistFilters({ supid: data.vendor.ps_Id });
      }
      
      if (data.transporter) {
        updateSelectBox('transporter', data.transporter);
      }

      // Set agent/salesman selection if available
      if (data.agentSalesman) {
        updateSelectBox('agentSalesman', data.agentSalesman);
      }

      // Set pricelist selection if pricelist data is available
      if (data.pricelist) {
        updateSelectBox('pricelist', data.pricelist);
      }

      // Set print type selection if print type data is available
      if (data.printType) {
        updateSelectBox('printType', data.printType);
      }

      // Populate lot data - use backend's lotData if available (for column orders)
      if (data.lotData && data.lotData.length > 0) {
        setLotData(data.lotData);
      }

      // Populate column order items if they are available from backend (when editing)
      if (data.columnOrderItems && data.columnOrderItems.length > 0) {
        // Backend already formatted the data for column order, but we need to map col indices
        const mappedColumnOrderItems = data.columnOrderItems.map(row => ({
          ...row,
          itemSizes: row.itemSizes || [], // Explicitly preserve itemSizes
          quantities: row.quantities.map(quantity => {
            // Generate column ID from size information and get the mapped column index
            const columnId = `${quantity.sz}_${quantity.szuom}`;
            const columnIndex = sizeToColumnMap.get(columnId);
            
            return {
              ...quantity,
              col: columnIndex !== undefined ? columnIndex : null
            };
          })
        }));
        
        setColumnOrderItems(mappedColumnOrderItems);
      } else if (data.orderDetails && data.orderDetails.length > 0) {
        // Fallback to original format transformation (shouldn't happen with isColumnOrder=true)
        populateColumnOrderItemsFromOrder(data.orderDetails);
      }
    }
  }, [data, sizeToColumnMap, ordTyp, setOrderHeader, updateSelectBox, setPricelistFilters, populateColumnOrderItemsFromOrder, setColumnOrderItems, setLotData]);

  // Reset to default column order items when creating a new order and set ordtyp
  useEffect(() => {
    if (!id) {
      setColumnOrderItems([
        {
          caseId: 1,
          item: null,
          quantities: [],
          itemSizes: []
        }
      ]);
      updateOrderHeader({ ordtyp: ordTyp });
    }
  }, [id, setColumnOrderItems, ordTyp, updateOrderHeader]);

  // Handle loading template data into column order form
  const handleLoadTemplate = useCallback((templateItems) => {
    if (!templateItems || templateItems.length === 0 || !sizeToColumnMap) return;

    const newColumnOrderItems = templateItems.map((templateRow) => {
      const item = {
        value: templateRow.baseItemId || templateRow.itemValue,
        label: templateRow.itemLabel,
        itemuom: templateRow.itemuom,
        itmbasicuom: templateRow.itmbasicuom,
        conval: templateRow.conval || 1,
      };

      const quantities = (templateRow.quantities || []).map((q) => {
        const columnId = `${q.sz}_${q.szuom}`;
        const columnIndex = sizeToColumnMap.get(columnId);
        return {
          ...q,
          qty: String(q.qty),
          col: columnIndex !== undefined ? columnIndex : null,
        };
      });

      return {
        caseId: 1,
        item,
        quantities,
        itemSizes: [],
      };
    });

    setColumnOrderItems(newColumnOrderItems);
  }, [sizeToColumnMap, setColumnOrderItems]);

  const handleSave = useCallback(async () => {
    try {
      // Transform column order items to individual items (like rate order structure)
      const filteredColumnOrderItems = [];
      
      columnOrderItems.forEach(row => {
        if (row.item && row.quantities && row.quantities.length > 0) {
          row.quantities.forEach(quantityEntry => {
            const qty = parseFloat(quantityEntry.qty);
            if (qty > 0 && quantityEntry.agitemdtlId) {
              const conval = parseFloat(row.item?.conval) || 1;
              const quantityInPCS = qty * conval;
              
              filteredColumnOrderItems.push({
                itemId: quantityEntry.agitemdtlId,
                caseId: row.caseId || 1,
                quantity: qty,
                quantityPcs: quantityInPCS,
                rate: 0,
                itemDetails: quantityEntry.itemDetails || ""
              });
            }
          });
        }
      });

      const orderData = {
        orderHeader,
        items: filteredColumnOrderItems,
        lotData,
      };

      if (id) {
        await updateOrderMutation.mutateAsync({ id, data: orderData });
        navigate('/order');
      } else {
        await createOrderMutation.mutateAsync(orderData);
        navigate('/order');
      }
    } catch (error) {
      console.error("Order save failed:", error);
    }
  }, [id, orderHeader, columnOrderItems, lotData, createOrderMutation, updateOrderMutation, navigate]);

  // Block order entry if FY order entry is disabled
  if (orderEntryBlocked) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-md bg-yellow-50 border border-yellow-200 p-6 text-center">
          <h2 className="text-lg font-semibold text-yellow-800">Order Entry Blocked</h2>
          <p className="mt-2 text-sm text-yellow-700">
            Order entry for this Financial Year is blocked. Please contact your administrator.
          </p>
          <button
            type="button"
            onClick={() => navigate('/order')}
            className="mt-4 rounded-md bg-yellow-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500"
          >
            Go to Orders
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while fetching order data
  if (id && isOrderLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-300 rounded w-1/3 mb-4"></div>
            <div className="grid grid-cols-3 gap-6">
              {Array.from({ length: 9 }).map((_, index) => (
                <div key={index} className="space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                  <div className="h-10 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Items table skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-300 px-6 py-3">
              <div className="h-6 bg-gray-400 rounded w-1/4"></div>
            </div>
            <div className="p-6 space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
          
          {/* Footer skeleton */}
          <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex justify-between items-center">
              <div className="h-4 bg-gray-300 rounded w-1/4"></div>
              <div className="h-10 bg-gray-300 rounded w-24"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SetOrderHeader 
        id={id} 
        title="Column Order Form"
        isLoading={isOrderLoading}
        formType={formType}
        onOpenLoadTemplate={() => setIsLoadTemplateOpen(true)}
        onOpenSaveTemplate={() => setIsSaveTemplateOpen(true)}
      />
      <SetColumnOrderDetails 
        id={id}
      />
      <SetOrderFooter 
        onSave={handleSave}
        isLoading={createOrderMutation.isPending || updateOrderMutation.isPending}
      />

      <LoadTemplateModal
        isOpen={isLoadTemplateOpen}
        onClose={() => setIsLoadTemplateOpen(false)}
        vendorId={selectBoxes.vendor?.ps_Id || ""}
        onLoadTemplate={handleLoadTemplate}
      />
      <SaveTemplateModal
        isOpen={isSaveTemplateOpen}
        onClose={() => setIsSaveTemplateOpen(false)}
        vendorId={selectBoxes.vendor?.ps_Id || ""}
        columnOrderItems={columnOrderItems}
      />
    </div>
  );
} 